import { supabase } from "@/integrations/supabase/client";

const KEY = "pending_invite_token";

export function savePendingInviteToken(token: string) {
  try { localStorage.setItem(KEY, token); } catch {}
}

export function readPendingInviteToken(): string | null {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

export function clearPendingInviteToken() {
  try { localStorage.removeItem(KEY); } catch {}
}

export interface AcceptInviteResult {
  ok: boolean;
  error?: string;
  agency_id?: string;
  role?: "owner" | "manager" | "staff";
}

/** Friendly message for a terminal accept_invite failure. Returns null for
 *  "already_accepted" — that means a prior attempt already succeeded, so it's
 *  not something the user needs to be alarmed about. */
export function describeInviteError(error: string): string | null {
  switch (error) {
    case "expired": return "That invitation has expired. Ask your team owner to resend it.";
    case "invalid_token": return "That invitation link is invalid.";
    case "email_mismatch": return "That invitation was sent to a different email address than the one you're signed in with.";
    case "already_in_another_agency": return "This account is already part of another agency and can't join a new one. Contact support if you need to merge or migrate accounts.";
    case "already_accepted": return null;
    default: return "We couldn't process your team invitation. Ask your team owner to resend it.";
  }
}

/**
 * Calls the accept_invite Postgres RPC. Caller must be authenticated and the
 * email on the invite must match the signed-in user's email.
 */
export async function redeemInviteToken(token: string): Promise<AcceptInviteResult> {
  const { data, error } = await (supabase as any).rpc("accept_invite", { _token: token });
  if (error) return { ok: false, error: error.message };
  return (data ?? { ok: false, error: "no_response" }) as AcceptInviteResult;
}

/**
 * If there is a stored invite token AND a logged-in session, try to redeem it.
 * Clears the stored token only on success or terminal failure (expired,
 * already_accepted, invalid_token, email_mismatch).
 */
export async function tryRedeemStoredInvite(): Promise<AcceptInviteResult | null> {
  const token = readPendingInviteToken();
  if (!token) return null;
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;
  const result = await redeemInviteToken(token);
  const terminal = result.ok || (result.error && [
    "expired",
    "already_accepted",
    "invalid_token",
    "email_mismatch",
    "already_in_another_agency",
  ].includes(result.error));
  if (terminal) clearPendingInviteToken();
  return result;
}
